import Text from "components/atoms/Text";
import { Navigate, Route, Routes } from "react-router-dom";
import { Button, COLOR_SYSTEM, Flex, Input, Static } from "components/atoms";
import styled from "styled-components";
import { useCallback, useRef, useState } from "react";
import { LogLevel, log } from "utils/LogUtil";
import { User } from "common/models";

const Container = styled.div`
  width: 100%;
  padding-top: 40px;
  padding-bottom: 40px;
  padding-left: 60px;
  padding-right: 130px;
`;

interface Props {
  me: User;
  onChange: (user: User) => void;
  onLogout: () => void;
  onChangePassword: () => void;
  isLoading: boolean;
}

const Account: React.FC<Props> = ({ me, onChange, onLogout, onChangePassword, isLoading }) => {
  const [name, setName] = useState<string>(me?.name ?? "");
  const [phone, setPhone] = useState<string>(me?.phone ?? "");
  const [company, setCompany] = useState<string>(me?.company ?? "");

  const phonError: boolean = !/^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/.test(phone);

  const handleUpdate = useCallback(() => {
    log(LogLevel.VERBOSE, "Account: handleUpdate", me, isLoading);
    onChange({ id: me.id, name, phone, company });
  }, [name, phone, company]);

  if (!me) return null;

  log(LogLevel.VERBOSE, "Account: render", me, isLoading);

  return (
    <Static>
      <Static>
        <Container>
          <Flex alignItems="center" customStyle={{ marginBottom: "20px" }}>
            <Text
              textType={"H1"}
              color={COLOR_SYSTEM.get("Gray")[900]}
              style={{ marginRight: "8px" }}
            >
              계정 관리
            </Text>
          </Flex>
          <Flex gap="20px" alignItems="flex-start">
            <Flex
              direction="column"
              gap="40px"
              customStyle={{
                marginBottom: "40px",
                background: COLOR_SYSTEM.get("Gray")[0],
                border: `1px solid ${COLOR_SYSTEM.get("Gray")[50]}`,
                borderRadius: "12px",
                overflow: "hidden",
                padding: "28px",
                flex: 1,
              }}
            >
              <Text textType={"H3"} color={COLOR_SYSTEM.get("Gray")[900]}>
                담당자 정보
              </Text>
              <Input
                label="담당자명"
                placeholder="홍길동"
                error={!name}
                value={name}
                disabled={true}
                disabledColor={true}
                onChange={(e) => setName(e.target.value)}
                hint={!name && "담당자 성함을 입력해주세요"}
              />
              <Input
                label="담당자 전화번호"
                required
                placeholder="01012345678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                error={phonError}
                hint={phonError && "전화번호를 잘못 입력하셨습니다."}
              />
              <Input
                label="회사명"
                placeholder="예)경영지원팀"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
              <Flex justifyContent="end">
                <Button
                  color="Primary"
                  size="Medium"
                  variant="Contained"
                  type="Text"
                  disabled={isLoading || !name || phonError}
                  disabledColor={isLoading || !name || phonError}
                  onClick={handleUpdate}
                >
                  저장
                </Button>
              </Flex>
            </Flex>
            <Flex
              direction="column"
              gap="40px"
              customStyle={{
                background: COLOR_SYSTEM.get("Gray")[0],
                border: `1px solid ${COLOR_SYSTEM.get("Gray")[50]}`,
                borderRadius: "12px",
                overflow: "hidden",
                padding: "28px",
                marginBottom: "20px",
                flex: 1,
              }}
            >
              <Text textType={"H3"} color={COLOR_SYSTEM.get("Gray")[900]}>
                로그인 정보
              </Text>
              <Input
                label="이메일 주소"
                required
                disabled
                disabledColor
                value={me.email}
                hint={"로그인 아이디로 사용됩니다."}
              />
              <Flex justifyContent="end" gap="8px">
                <Button
                  color="Secondary"
                  size="Medium"
                  variant="Tinted"
                  type="Text"
                  disabled={isLoading}
                  disabledColor={isLoading}
                  onClick={onLogout}
                >
                  로그아웃
                </Button>
                <Button
                  color="Secondary"
                  size="Medium"
                  variant="Tinted"
                  type="Text"
                  disabled={isLoading}
                  disabledColor={isLoading}
                  onClick={onChangePassword}
                >
                  비밀번호 재설정
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Container>
      </Static>
    </Static>
  );
};

export default Account;
